import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getForumNotifications, markForumNotificationsRead } from '../services/forumApi';
import { onUnifiedNotification } from '../services/socket';
import { authenticatedRequest } from '../services/authApi';
import {
  API_ORIGIN,
  resolveUserIdSafe,
  _groupFeedNotifItems,
} from '../utils/homeHelpers';

const createEmptyFeedPreview = (translate) => ({
  icon: '🪐',
  label: typeof translate === 'function' ? translate('notifications.feedActivity') : 'Actividad del feed',
  text: '',
  time: '',
  type: '',
  actorName: '',
  avatarUrl: '',
  count: 0,
});

const createEmptyForumPreview = () => ({
  text: '',
  actorName: '',
  time: '',
});

const useHomeNotifications = ({ user, token, isAuthReady, location }) => {
  const { t } = useTranslation('home');
  const [forumUnreadCount, setForumUnreadCount] = useState(0);
  const [forumNotifPreview, setForumNotifPreview] = useState(createEmptyForumPreview);
  const [feedUnreadCount, setFeedUnreadCount] = useState(0);
  const [feedNotifItems, setFeedNotifItems] = useState([]);
  const [feedNotifPreview, setFeedNotifPreview] = useState(() => createEmptyFeedPreview(t));
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  const currentUserId = resolveUserIdSafe(user);
  const chatUnreadRequestRef = useRef(null);

  const fetchForumUnread = useCallback(async () => {
    try {
      if (!isAuthReady || !currentUserId || !token) {
        setForumUnreadCount(0);
        setForumNotifPreview(createEmptyForumPreview());
        return;
      }
      const data = await getForumNotifications({ unread: true, limit: 1 });
      if (data && data.ok) {
        const c = Number(data.unreadCount || 0);
        setForumUnreadCount(Number.isFinite(c) ? Math.max(0, c) : 0);
        const preview = Array.isArray(data.items) ? data.items[0] : null;
        setForumNotifPreview(preview ? {
          text: String(preview.message || ''),
          actorName: String(preview.fromName || ''),
          time: String(preview.createdAt || ''),
        } : createEmptyForumPreview());
      }
    } catch {}
  }, [isAuthReady, currentUserId, token]);

  const fetchFeedUnread = useCallback(async () => {
    try {
      if (!isAuthReady || !currentUserId || !token) { setFeedUnreadCount(0); return; }

      const r = await authenticatedRequest(`${API_ORIGIN}/api/feed/notifications/unread-count`, {
        rawResponse: true,
        credentials: 'include',
        cache: 'no-store',
        diagSource: 'useHomeNotifications.feedUnread',
      });

      if (!r.ok) {
        return;
      }

      const j = await r.json().catch(() => null);
      const c = Number(j?.unreadCount ?? 0);
      setFeedUnreadCount(Number.isFinite(c) ? Math.max(0, c) : 0);
    } catch {}
  }, [isAuthReady, currentUserId, token]);

  const fetchFeedNotifPreview = useCallback(async () => {
    try {
      if (!isAuthReady || !currentUserId || !token) {
        setFeedNotifItems([]);
        setFeedNotifPreview(createEmptyFeedPreview(t));
        return;
      }

      const r = await authenticatedRequest(`${API_ORIGIN}/api/feed/notifications?limit=12`, {
        rawResponse: true,
        credentials: 'include',
        cache: 'no-store',
        diagSource: 'useHomeNotifications.feedPreview',
      });

      if (!r.ok) {
        return;
      }

      const j = await r.json().catch(() => null);
      const rawItems = Array.isArray(j?.items) ? j.items : [];
      const unreadTarget = Math.max(0, Number(j?.unreadCount ?? 0));

      if (unreadTarget <= 0) {
        setFeedNotifItems([]);
        setFeedNotifPreview(createEmptyFeedPreview(t));
        return;
      }

      const unreadOnly = rawItems.filter((item) => {
        const v = item?.isRead ?? item?.read ?? item?.seen;
        return v === false || v === 0 || v === 'false';
      });

      const sourceItems = unreadOnly.length ? unreadOnly : rawItems.slice(0, unreadTarget);
      const grouped = _groupFeedNotifItems(sourceItems, currentUserId, t);
      const visibleItems = grouped.slice(0, unreadTarget);

      setFeedNotifItems(visibleItems);

      const topGroup = visibleItems[0] || null;
      if (!topGroup) {
        setFeedNotifPreview(createEmptyFeedPreview(t));
        return;
      }

      setFeedNotifPreview({
        icon: topGroup.familyIcon || '🪐',
        label: topGroup.familyLabel || t('notifications.feedActivity'),
        text: topGroup.subtitle || '',
        time: topGroup.time || '',
        type: String(topGroup.family || ''),
        actorName: topGroup.actorText || '',
        avatarUrl: topGroup.avatarUrl || '',
        count: Number(topGroup.count || 0),
      });
    } catch {}
  }, [isAuthReady, currentUserId, token, t]);

  const markFeedNotificationsReadAll = useCallback(async () => {
    try {
      if (!isAuthReady || !currentUserId || !token) {
        setFeedUnreadCount(0);
        setFeedNotifItems([]);
        setFeedNotifPreview(createEmptyFeedPreview(t));
        return false;
      }

      const r = await authenticatedRequest(`${API_ORIGIN}/api/feed/notifications/read-all`, {
        rawResponse: true,
        method: 'POST',
        credentials: 'include',
        diagSource: 'useHomeNotifications.feedReadAll',
      });

      if (!r.ok) return false;

      const j = await r.json().catch(() => null);
      const c = Number(j?.unreadCount ?? 0);
      setFeedUnreadCount(Number.isFinite(c) ? Math.max(0, c) : 0);

      if (Number(j?.unreadCount ?? 0) <= 0) {
        setFeedNotifItems([]);
        setFeedNotifPreview(createEmptyFeedPreview(t));
      }
      return true;
    } catch {
      return false;
    }
  }, [isAuthReady, currentUserId, token, t]);

  const fetchChatUnread = useCallback(async () => {
    // The user id only gates UI state. The route derives ownership exclusively
    // from the verified Bearer token; it is never sent as a query parameter.
    if (!isAuthReady || !currentUserId || !token) {
      setChatUnreadCount(0);
      return undefined;
    }

    const requestKey = `${currentUserId}:${token}`;
    if (chatUnreadRequestRef.current?.key === requestKey) {
      return chatUnreadRequestRef.current.promise;
    }

    const promise = (async () => {
      try {
        // Temporary, opt-in browser diagnostic. It proves readiness and token
        // presence without exposing the JWT itself in production consoles.
        if (typeof window !== 'undefined' && window.localStorage?.getItem('z10:auth_trace') === '1') {
          console.info('[auth-trace] home-chat-unread', {
            isAuthReady,
            currentUserId,
            tokenPresent: Boolean(token),
            tokenShape: String(token).split('.').length === 3 ? 'jwt_like' : 'invalid_shape',
          });
        }
        const r = await authenticatedRequest(`${API_ORIGIN}/api/chat-notifications/unread-count`, {
          rawResponse: true,
          credentials: 'include',
          cache: 'no-store',
          diagSource: 'useHomeNotifications.chatUnread',
        });
        if (!r.ok) return;

        const j = await r.json().catch(() => null);
        const c = Number(j?.unreadCount ?? j?.count ?? j?.data?.unreadCount ?? 0);
        setChatUnreadCount(Number.isFinite(c) ? Math.max(0, c) : 0);
      } catch {}
    })();

    chatUnreadRequestRef.current = { key: requestKey, promise };
    try {
      return await promise;
    } finally {
      if (chatUnreadRequestRef.current?.promise === promise) {
        chatUnreadRequestRef.current = null;
      }
    }
  }, [isAuthReady, currentUserId, token]);

  const refreshNotifications = useCallback(async () => {
    await Promise.all([
      fetchForumUnread(),
      fetchFeedUnread(),
      fetchFeedNotifPreview(),
      fetchChatUnread(),
    ]);
  }, [fetchForumUnread, fetchFeedUnread, fetchFeedNotifPreview, fetchChatUnread]);

  const markAllNotificationsRead = useCallback(async () => {
    if (!isAuthReady || !currentUserId || !token) return false;

    const results = await Promise.allSettled([
      markForumNotificationsRead({ all: true }).then((result) => {
        if (!result?.ok) throw new Error('forum_notification_mark_all_failed');
        return result;
      }),
      markFeedNotificationsReadAll(),
      authenticatedRequest(`${API_ORIGIN}/api/chat-notifications/mark-all-read`, {
        rawResponse: true,
        method: 'POST',
        credentials: 'include',
        diagSource: 'useHomeNotifications.chatReadAll',
      }).then((response) => {
        if (!response.ok) throw new Error('chat_notification_mark_all_failed');
        return response.json().catch(() => null);
      }),
    ]);

    await refreshNotifications();
    return results.every((result) => result.status === 'fulfilled' && result.value !== false);
  }, [isAuthReady, currentUserId, token, markFeedNotificationsReadAll, refreshNotifications]);

  useEffect(() => {
    if (location?.pathname !== '/home') return;
    refreshNotifications();
  }, [location?.pathname, refreshNotifications]);

  useEffect(() => {
    if (!user) {
      setChatUnreadCount(0);
      return;
    }

    let off = null;
    try {
      off = onUnifiedNotification((payload) => {
        try {
          if (!payload || typeof payload !== 'object') return;

          const kind = String(payload.kind || payload.type || payload.source || payload.channel || '').toLowerCase();
          const isChat = kind.includes('chat') || kind.includes('message') || kind.includes('dm');
          if (!isChat) return;

          if (payload.unreadCount != null) {
            const n = Number(payload.unreadCount);
            if (Number.isFinite(n)) {
              setChatUnreadCount(Math.max(0, n));
              return;
            }
          }

          setChatUnreadCount((prev) => {
            const p = Number(prev || 0);
            return Number.isFinite(p) ? p + 1 : 1;
          });
        } catch {}
      });
    } catch {}

    return () => {
      try {
        if (typeof off === 'function') off();
      } catch {}
    };
  }, [user]);

  const unifiedUnreadCount = useMemo(() => {
    const a = Number(forumUnreadCount || 0);
    const b = Number(chatUnreadCount || 0);
    const c = Number(feedUnreadCount || 0);
    return Math.max(
      0,
      (Number.isFinite(a) ? a : 0) +
      (Number.isFinite(b) ? b : 0) +
      (Number.isFinite(c) ? c : 0)
    );
  }, [forumUnreadCount, chatUnreadCount, feedUnreadCount]);

  const [notifMenuOpen, setNotifMenuOpen] = useState(false);
  const [notifAnchorRect, setNotifAnchorRect] = useState(null);
  const bellWrapRef = useRef(null);

  const computeBellRect = useCallback(() => {
    try {
      const el = bellWrapRef.current;
      if (!el || typeof el.getBoundingClientRect !== 'function') return null;
      const r = el.getBoundingClientRect();
      return {
        left: r.left,
        top: r.top,
        right: r.right,
        bottom: r.bottom,
        width: r.width,
        height: r.height,
      };
    } catch {
      return null;
    }
  }, []);

  const openNotifMenu = useCallback(() => {
    const r = computeBellRect();
    setNotifAnchorRect(r);
    setNotifMenuOpen(true);
  }, [computeBellRect]);

  const closeNotifMenu = useCallback(({ restoreFocus = false } = {}) => {
    setNotifMenuOpen(false);
    if (restoreFocus) {
      setTimeout(() => {
        try { bellWrapRef.current?.focus?.(); } catch {}
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!notifMenuOpen) return;
    const onUpdate = () => setNotifAnchorRect(computeBellRect());
    try {
      window.addEventListener('resize', onUpdate, { passive: true });
      window.addEventListener('orientationchange', onUpdate, { passive: true });
      window.addEventListener('scroll', onUpdate, { passive: true });
    } catch {}
    return () => {
      try {
        window.removeEventListener('resize', onUpdate);
        window.removeEventListener('orientationchange', onUpdate);
        window.removeEventListener('scroll', onUpdate);
      } catch {}
    };
  }, [notifMenuOpen, computeBellRect]);

  const handleForumBellClick = useCallback(() => {
    if (notifMenuOpen) {
      closeNotifMenu();
      return;
    }
    openNotifMenu();
  }, [notifMenuOpen, openNotifMenu, closeNotifMenu]);

  return {
    forumUnreadCount,
    forumNotifPreview,
    feedUnreadCount,
    feedNotifItems,
    feedNotifPreview,
    chatUnreadCount,
    fetchForumUnread,
    fetchFeedUnread,
    fetchFeedNotifPreview,
    refreshNotifications,
    markFeedNotificationsReadAll,
    markAllNotificationsRead,
    fetchChatUnread,
    unifiedUnreadCount,
    notifMenuOpen,
    notifAnchorRect,
    bellWrapRef,
    openNotifMenu,
    closeNotifMenu,
    handleForumBellClick,
  };
};

export default useHomeNotifications;
